import { getDB } from "./schema.js";
import { nowIso } from "../utils/dates.js";
import { queueHistoryWrite } from "./sync.js";
import { historyEventsFor, mergeEvent } from "../utils/history.js";

// History — the activity log's read/write half (design_documents/history.md;
// utils/history.js has the pure core). Every item change in the six media
// modules calls logItemChange(), which records what changed, folds quick
// corrections into the previous event, and queues the event for cloud sync.
// Logging must never break the write it describes: failures are swallowed.

const STORE = "history";

export const HISTORY_EVENT = "history:changed";

function emitChanged() {
	try {
		window.dispatchEvent(new Event(HISTORY_EVENT));
	} catch {
		// no window (tests)
	}
}

// The item's latest event. Two changes can land in the same millisecond (an
// add followed at once by an update — imports do this); on such a tie the
// event of the same kind wins, so a quick correction still folds into it.
async function lastEventFor(db, itemId, kind = null) {
	const events = await db.getAllFromIndex(STORE, "item_id", itemId);
	let last = null;
	for (const e of events) {
		if (e.deleted) continue;
		if (!last || e.at > last.at || (e.at === last.at && e.kind === kind && last.kind !== kind)) last = e;
	}
	return last;
}

// Record the events for one change. `before` null = added, `after` null = removed.
export async function logItemChange(mediaType, before, after) {
	try {
		const events = historyEventsFor(mediaType, before, after, nowIso());
		if (events.length === 0) return [];
		const db = await getDB();
		const written = [];
		for (const ev of events) {
			const stamped = { ...ev, created_at: ev.at, updated_at: ev.at, _synced: false };
			const merge = mergeEvent(await lastEventFor(db, ev.item_id, ev.kind), stamped);
			if (merge.action === "append") {
				await db.put(STORE, stamped);
				written.push(stamped);
				await queueHistoryWrite(stamped);
			} else if (merge.action === "replace") {
				const next = { ...merge.event, updated_at: stamped.at, _synced: false };
				await db.put(STORE, next);
				written.push(next);
				await queueHistoryWrite(next);
			} else if (merge.action === "drop") {
				// The change undid the previous one: tombstone it (synced as deleted).
				const prev = await db.get(STORE, merge.id);
				if (prev) {
					const dead = { ...prev, deleted: true, updated_at: stamped.at, _synced: false };
					await db.put(STORE, dead);
					await queueHistoryWrite(dead);
				}
			}
		}
		emitChanged();
		return written;
	} catch (err) {
		console.warn("History logging failed (the change itself was saved):", err);
		return [];
	}
}

export async function listHistory() {
	const db = await getDB();
	return db.getAll(STORE);
}

export async function listItemHistory(itemId) {
	const db = await getDB();
	const events = await db.getAllFromIndex(STORE, "item_id", itemId);
	return events.filter((e) => !e.deleted).sort((a, b) => b.at.localeCompare(a.at));
}

// Remove an event from history (a tombstone, so other devices drop it too).
export async function removeHistoryEvent(id) {
	const db = await getDB();
	const e = await db.get(STORE, id);
	if (!e) return null;
	const next = { ...e, deleted: true, updated_at: nowIso(), _synced: false };
	await db.put(STORE, next);
	await queueHistoryWrite(next);
	emitChanged();
	return next;
}

// Change when an event happened ("I actually finished this in 2021"). `date`
// is YYYY-MM-DD (kept at midday UTC so it reads as that day everywhere). When it
// is the item's earliest Finished event, the item's first-finished date follows.
export async function setHistoryEventDate(id, date) {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(date ?? "")) throw new Error("Pick a date.");
	const db = await getDB();
	const e = await db.get(STORE, id);
	if (!e) return null;
	const next = { ...e, at: `${date}T12:00:00.000Z`, reconstructed: false, updated_at: nowIso(), _synced: false };
	await db.put(STORE, next);
	await queueHistoryWrite(next);

	if (e.kind === "engagement" && e.to_value === "done" && e.item_id) {
		const finishes = (await db.getAllFromIndex(STORE, "item_id", e.item_id))
			.filter((x) => !x.deleted && x.kind === "engagement" && x.to_value === "done")
			.sort((a, b) => a.at.localeCompare(b.at));
		if (finishes[0]?.id === id) await syncFirstFinished(e.media_type, e.item_id, date);
	}
	emitChanged();
	return next;
}

// Keep the item's date_first_completed in line with an edited first finish.
// Imported lazily: the media modules import this one.
async function syncFirstFinished(mediaType, itemId, date) {
	const modules = {
		videogame: () => import("./games.js").then((m) => m.updateGame),
		book: () => import("./books.js").then((m) => m.updateBook),
		tv: () => import("./tv.js").then((m) => m.updateTvSeries),
		anime: () => import("./anime.js").then((m) => m.updateAnime),
		movie: () => import("./movies.js").then((m) => m.updateMovie),
		comic: () => import("./comics.js").then((m) => m.updateComic),
	};
	const load = modules[mediaType];
	if (!load) return;
	try {
		const update = await load();
		await update(itemId, { date_first_completed: date });
	} catch {
		// item gone — the event keeps its new date regardless
	}
}

// An undone delete shouldn't leave "Removed from your library" behind: tombstone
// the item's newest removal event (synced as deleted).
export async function forgetLastRemoval(itemId) {
	const db = await getDB();
	const removals = (await db.getAllFromIndex(STORE, "item_id", itemId))
		.filter((e) => e.kind === "removed" && !e.deleted)
		.sort((a, b) => b.at.localeCompare(a.at));
	const last = removals[0];
	if (!last) return null;
	const next = { ...last, deleted: true, updated_at: nowIso(), _synced: false };
	await db.put(STORE, next);
	await queueHistoryWrite(next);
	emitChanged();
	return next;
}
